<template>
  <div class="w-full px-3 py-2 flex" :class="classes">
    <div
      class="min-w-[40px] max-w-[40px] min-h-[40px] max-h-[40px] mr-3 p-0.5 rounded-sm bg-gray-200/20 relative"
    >
      <ThumbnailIcon
        v-if="file?.type"
        class="w-full h-full"
        :image-url="applyAuthorizationToken(notification.primaryIcon.imageUrl)"
        :icon-size="36"
        :type="file.type"
      />
    </div>
    <div
      class="font-secondary text-[14px] leading-5 tablet-h:leading-[19px] tablet-h:text-[13px] text-gray-800 -mt-1 flex flex-col justify-between"
    >
      <component :is="copy" class="mb-1 tablet-h:mb-0.5" />
      <div class="flex items-center justify-between">
        <div class="text-[13px] tablet-h:text-[12px] opacity-50">
          {{ formatDistanceToNowStrict(props.notification.timestamp) }} ago
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NotificationType,
  type File,
  type FileMetadata,
  type InviteMetadata,
  type Notification,
} from '@/shared/graphql/legacy/generated'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { ThumbnailIcon } from '@dacr/ui'
import { formatDistanceToNowStrict } from 'date-fns'
import { computed, h } from 'vue'
import { RouterLink } from 'vue-router'

const props = defineProps<{
  notification: Notification
}>()

const classes = computed(() => ({
  'tablet-h:hover:bg-[#FAFAFA]': props.notification.read,
  'bg-green-900/10': !props.notification.read,
}))
const metadata = computed(() => props.notification.metadata as FileMetadata)

const inviteMetadata = computed(
  () => props.notification.metadata as InviteMetadata
)

const file = computed(() => metadata.value.file as File)

const copy = computed(() => {
  if (props.notification.type === NotificationType.FileOwnerAccessRemoved) {
    return h('div', [
      `You have been removed as one of the owners for the copright of `,
      h(
        RouterLink,
        {
          to: `/file/${file.value?.copyrightId}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => file.value.fileName
      ),
      '.',
    ])
  }

  if (props.notification.type === NotificationType.FileAuthorAccessRemoved) {
    return h('div', [
      `You have been removed as one of the authors for the copright of `,
      h(
        RouterLink,
        {
          to: `/file/${file.value?.copyrightId}`,
          class: 'font-medium text-green-900 break-all',
        },
        () => file.value.fileName
      ),
      '.',
    ])
  }

  if (
    props.notification.type === NotificationType.TeamMemberAccessRemoved &&
    inviteMetadata.value.invite.reference.__typename === 'Origin'
  ) {
    return h('div', [
      `You have been removed as one of the ${inviteMetadata.value.invite.reference.name} team members.`,
    ])
  }

  return null
})
</script>
